import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, User, Calendar, ChevronLeft, LogOut, Edit2, Save, X, Camera, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showError, showSuccess } from "../../utils/toastUtils";
import uploadFileToCloudinary from "../../utils/uploadFileToCloudinary";
import logo from "../../Assets/Images/FooterImages/Logo1.webp";


const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;

  return (
    <div className="flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition">
      <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-blue-50 flex-shrink-0">
        <Icon size={16} className="md:w-[18px] md:h-[18px] text-blue-600" />
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-[10px] md:text-xs text-gray-500 font-medium">{label}</span>
        <span className="text-sm md:text-base font-semibold text-gray-800 break-words mt-0.5">
          {value}
        </span>
      </div>
    </div>
  );
};

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const UserProfile = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    profilePicture: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        profilePicture: user.profilePicture || "",
      });
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showError("Please upload an image file");
      return;
    }

    try {
      setUploadingPhoto(true);
      setUploadProgress(0);

      const { uploadPromise } = uploadFileToCloudinary(file, (progress) => {
        setUploadProgress(progress);
      });

      const result = await uploadPromise;

      if (result?.url) {
        setFormData((prev) => ({ ...prev, profilePicture: result.url }));
        showSuccess("Photo uploaded successfully");
      }
    } catch (error) {
      showError("Failed to upload photo");
      console.error(error);
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name?.trim()) {
      showError("Name is required");
      return;
    }

    if (!formData.email?.trim()) {
      showError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError("Please enter a valid email");
      return;
    }

    if (!formData.phoneNumber?.trim()) {
      showError("Phone number is required");
      return;
    }

    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      showError("Phone number must be 10 digits");
      return;
    }

    try {
      setIsSaving(true);

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update/${user._id}`,
        formData
      );

      if (response.data.success) {
        const updatedUser = response.data.data;

        setUser(updatedUser);
        localStorage.setItem("userData", JSON.stringify(updatedUser));

        showSuccess("Profile updated successfully");
        setIsEditing(false);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update profile";
      showError(message);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };
  // console.log(user)

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: user.name || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      profilePicture: user.profilePicture || "",
    });
    setIsEditing(false);
  };

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  const displayPhoto = isEditing ? formData.profilePicture : user.profilePicture;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 pb-20 md:pb-6">
      {/* Header - App-like sticky header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-lg border-b shadow-sm">
        <div className="flex items-center gap-3 px-3 md:px-4 py-3 md:py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-full transition"
            aria-label="Go back"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg md:text-xl font-bold text-gray-900">My Profile</h1>
            <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
              {isEditing ? "Edit your account details" : "View your account details"}
            </p>
          </div>

          {/* Edit/Save/Cancel Buttons */}
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-3 md:px-4 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 active:scale-95 transition text-sm font-medium"
            >
              <Edit2 size={14} className="md:w-4 md:h-4" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          ) : (
            <div className="flex gap-1.5 md:gap-2">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-2.5 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95 transition disabled:opacity-50 text-sm"
              >
                <X size={14} className="md:w-4 md:h-4" />
                <span className="hidden sm:inline">Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-2.5 md:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:scale-95 transition disabled:opacity-50 text-sm"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={14} className="md:w-4 md:h-4 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={14} className="md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Save</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 md:px-4 py-4 md:py-6 space-y-4 md:space-y-5">
        {/* Profile Card with Banner - App-like design */}
        <div className="bg-white rounded-2xl md:rounded-3xl border shadow-md overflow-hidden">
          {/* Gradient Banner */}
          <div style={{ backgroundImage: `url(${logo})` }} className="h-24 md:h-32 bg-contain bg-no-repeat bg-center opacity-20 relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent)]"></div>
          </div>

          <div className="px-4 md:px-6 pb-5 md:pb-6">
            {/* Profile Avatar - Overlapping design */}
            <div className="relative -mt-12 md:-mt-16 mb-4">
              <div className="inline-block relative">
                {displayPhoto ? (
                  <img
                    src={displayPhoto}
                    alt={user.name}
                    onClick={() => setPreviewImage(displayPhoto)}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl object-cover bg-gray-100 cursor-pointer hover:opacity-90 transition"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-primaryColor to-accentTextColor text-white flex items-center justify-center text-3xl md:text-5xl font-bold">
                    {(isEditing ? formData.name : user.name)?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}

                {/* Online indicator dot */}
                {!isEditing && (
                  <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 w-4 h-4 md:w-5 md:h-5 bg-green-500 border-4 border-white rounded-full"></div>
                )}

                {/* Camera button for edit mode */}
                {isEditing && (
                  <label className="absolute bottom-0 right-0 md:bottom-1 md:right-1 w-8 h-8 md:w-10 md:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 active:scale-95 transition shadow-lg">
                    {uploadingPhoto ? (
                      <Loader2 size={16} className="md:w-[18px] md:h-[18px] animate-spin" />
                    ) : (
                      <Camera size={16} className="md:w-[18px] md:h-[18px]" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}

                {uploadingPhoto && (
                  <div className="absolute -bottom-6 left-0 right-0 text-center">
                    <div className="text-xs text-blue-600 font-medium">
                      {uploadProgress}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* User Info */}
            <div>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="text-xl md:text-2xl font-bold text-gray-900 border-b-2 border-blue-600 focus:outline-none bg-transparent w-full mb-2"
                  placeholder="Your Name"
                />
              ) : (
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 break-words">
                  {user.name || "User"}
                </h2>
              )}

              <p className="text-xs md:text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                <Calendar size={12} className="md:w-3.5 md:h-3.5" />
                <span className="hidden sm:inline">Member since {formatDate(user.createdAt)}</span>
                <span className="sm:hidden">
                  Since {new Date(user.createdAt).toLocaleDateString("en-IN", {
                    month: "short",
                    year: "numeric"
                  })}
                </span>
              </p>

              {/* Quick Stats */}
              {!isEditing && (
                <div className="flex gap-2 mt-3">
                  <div className="flex-1 bg-blue-50 rounded-lg p-2.5 md:p-3">
                    <p className="text-[10px] md:text-xs text-blue-600 font-medium">Status</p>
                    <p className="text-xs md:text-sm font-bold text-blue-700 mt-0.5">Active</p>
                  </div>
                  <div className="flex-1 bg-green-50 rounded-lg p-2.5 md:p-3">
                    <p className="text-[10px] md:text-xs text-green-600 font-medium">Account Type</p>
                    <p className="text-xs md:text-sm font-bold text-green-700 mt-0.5">User</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information - Card Style */}
        <div className="bg-white rounded-2xl md:rounded-3xl border shadow-md p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4 md:mb-5">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail size={16} className="md:w-[18px] md:h-[18px] text-blue-600" />
            </div>
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              Contact Information
            </h3>
          </div>

          <div className="space-y-1">
            {isEditing ? (
              <>
                {/* Email Input */}
                <div className="p-2.5 md:p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition">
                  <label className="text-[10px] md:text-xs text-gray-500 font-medium block mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full text-xs md:text-sm font-semibold text-gray-800 focus:outline-none bg-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Phone Input */}
                <div className="p-2.5 md:p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition">
                  <label className="text-[10px] md:text-xs text-gray-500 font-medium block mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    maxLength={10}
                    className="w-full text-xs md:text-sm font-semibold text-gray-800 focus:outline-none bg-transparent"
                    placeholder="10-digit mobile number"
                  />
                </div>
              </>
            ) : (
              <>
                <InfoRow icon={Mail} label="Email Address" value={user.email} />
                <InfoRow icon={Phone} label="Phone Number" value={user.phoneNumber} />
              </>
            )}
          </div>
        </div>

        {/* Personal Details - Card Style */}
        <div className="bg-white rounded-2xl md:rounded-3xl border shadow-md p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4 md:mb-5">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-purple-100 flex items-center justify-center">
              <User size={16} className="md:w-[18px] md:h-[18px] text-purple-600" />
            </div>
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              Personal Details
            </h3>
          </div>

          <div className="space-y-1">
            <InfoRow icon={User} label="Full Name" value={user.name} />
            <InfoRow icon={MapPin} label="Address" value={user.address} />
            <InfoRow
              icon={Calendar}
              label="Account Created"
              value={formatDate(user.createdAt)}
            />
          </div>
        </div>

        {/* Logout Button - Prominent */}
        <div className="bg-white rounded-2xl md:rounded-3xl border shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
            Account Actions
          </h3>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3 md:py-3.5 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 active:scale-[0.98] transition font-semibold text-sm md:text-base"
          >
            <LogOut size={18} className="md:w-5 md:h-5" />
            <span>Logout from Account</span>
          </button>
        </div>
      </div>


      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition"
            >
              <X size={28} />
            </button>

            {/* Image */}
            <img
              src={previewImage}
              alt="Profile Preview"
              className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl bg-black"
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;